/**
 * DataRepository - New Data Layer
 *
 * Replaces database.ts with sync-aware operations.
 *
 * Key features:
 * - All mutations automatically queue for sync
 * - Emits events on all data changes
 * - Single source of truth
 * - Works with both local IndexedDB and Supabase
 * - Handles the new context model (vs old links)
 *
 * Architecture:
 * Component → DataRepository → IndexedDB + SyncEngine → Supabase
 */

import { db, generateId, now } from '../db/database';
import { syncEngine } from '../sync/SyncEngine';
import { eventBus } from '../events/EventBus';
import { supabase } from '../lib/supabase';
import { restSelect, restUpdate } from '../lib/supabaseRest';
import type { PKMObject, Property, Link, Attachment, ObjectTemplate, Block, ObjectStatus, DistillationLevel, UserCollection, UserCollectionItem, TemplateBlock } from '../models/types';
import { propertyRelationService } from '../services/PropertyRelationService';
import { propertyRollupService } from '../services/PropertyRollupService';
import { propertyFormulaService } from '../services/PropertyFormulaService';
import { propertyOptionService } from '../services/PropertyOptionService';
import { propertyComputedCacheService } from '../services/PropertyComputedCacheService';
import { autoRelationService } from '../services/AutoRelationService';
import { autoEmbedQueue } from '../services/ai/AutoEmbedQueue';
import { setBlockContent, initYDocFromState } from '../services/BlockYjsService';
import type {
  PropertyRelation,
  PropertyRollup,
  PropertyFormula,
  PropertyOption,
  CreateRelationRequest,
  CreateRollupRequest,
  CreateFormulaRequest,
  CreateOptionRequest,
  ComputedCacheEntry,
} from '../types/advanced-properties';
import type { BlockReminder } from '../types/reminders';

/**
 * Context represents the new linking model
 * Replaces old Link type with hybrid context approach
 */
export interface Context {
  id: string;
  object_id: string; // The object being linked
  context_id: string; // The context it belongs to
  context_type: 'primary' | 'appearance' | 'reference';
  strength: 'strong' | 'weak';
  order_index?: number;
  created_at: string;
}

class DataRepository {
  private isOnline: boolean = navigator.onLine;

  // Debounce timers for sync queue operations
  private blockSyncDebounce: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private objectSyncDebounce: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private readonly SYNC_DEBOUNCE_MS = 300; // Wait 300ms after last keystroke before queuing sync

  // Field-level merge: accumulate which object columns changed across the
  // debounce window so the queued UPDATE is a DELTA of only the changed columns
  // (not the whole row). A name-edit then a status-edit within 300ms both land
  // in the same delta; an unrelated column the user didn't touch is never sent,
  // so a concurrent edit to that column on another device survives.
  private objectDirtyColumns: Map<string, Set<string>> = new Map();
  // Object columns eligible for delta sync. Mirrors OBJECT_DELTA_COLUMNS_WEB in
  // mobile/src/lib/syncColumns.ts (camelCase here; queueChange snake-cases on
  // the way out, e.g. sourceType -> source_type). `updated_at` is appended to
  // every delta separately and is intentionally not in this set.
  private readonly OBJECT_DELTA_COLUMNS = new Set([
    'type', 'name', 'content', 'status', 'color', 'tags', 'sourceType', 'distillation',
  ]);

  constructor() {
    // Track online status
    window.addEventListener('online', () => {
      this.isOnline = true;
    });
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
  }

  /**
   * Get user ID from localStorage - avoids supabase.auth.getUser() which can hang
   * Returns null if not authenticated
   */
  private getUserIdFromLocalStorage(): string | null {
    try {
      const storageKey = 'sb-urkfvgqpzeuixgdayrex-auth-token';
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;
      const parsed = JSON.parse(stored);
      return parsed.user?.id || null;
    } catch {
      return null;
    }
  }

  /**
   * Debounced sync queue for blocks - only queues after user stops typing
   */
  private debouncedBlockSync(blockId: string, syncFn: () => Promise<void>): void {
    // Clear any existing timer for this block
    const existingTimer = this.blockSyncDebounce.get(blockId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set new timer - will queue sync after debounce period
    const timer = setTimeout(async () => {
      this.blockSyncDebounce.delete(blockId);
      await syncFn();
    }, this.SYNC_DEBOUNCE_MS);

    this.blockSyncDebounce.set(blockId, timer);
  }

  /**
   * Debounced sync queue for objects - only queues after user stops typing
   */
  private debouncedObjectSync(objectId: string, syncFn: () => Promise<void>): void {
    // Clear any existing timer for this object
    const existingTimer = this.objectSyncDebounce.get(objectId);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    // Set new timer - will queue sync after debounce period
    const timer = setTimeout(async () => {
      this.objectSyncDebounce.delete(objectId);
      await syncFn();
    }, this.SYNC_DEBOUNCE_MS);

    this.objectSyncDebounce.set(objectId, timer);
  }

  // ==================== OBJECTS ====================

  /**
   * Create a new object
   */
  async createObject(
    name: string,
    type: PKMObject['type'],
    content: string = '',
    color?: string,
    status: PKMObject['status'] = 'active',
    sourceType?: PKMObject['sourceType']
  ): Promise<PKMObject> {
    // Get current user ID from localStorage (avoids hanging on supabase.auth.getUser())
    const userId = this.getUserIdFromLocalStorage();

    // Create object locally (instant)
    const object: PKMObject = {
      id: generateId(),
      name,
      type,
      content,
      color,
      status, // Use provided status or default to 'active'
      sourceType, // Subtype for sources (e.g. 'web', 'email') — gates reader view
      distillation: 0,  // Default to raw/unprocessed
      properties: [],
      createdAt: now(),
      updatedAt: now(),
      // CRITICAL SECURITY FIX: Set userId BEFORE adding to IndexedDB
      // This ensures the defensive filter in getAllObjects() works correctly
      // and prevents cross-user data contamination
      userId: userId || undefined,
    };

    await db.objects.add(object);

    // Only queue for sync if user is authenticated
    console.log('[DataRepository.createObject] User authenticated:', !!userId, 'Object ID:', object.id);
    if (userId) {
      console.log('[DataRepository.createObject] Queueing object create for sync');
      await syncEngine.queueChange('objects', 'create', object.id, {
        name: object.name,
        type: object.type,
        content: object.content,
        color: object.color,
        status: object.status,
        source_type: object.sourceType ?? null,
        user_id: userId,  // Required for RLS policy
        distillation: object.distillation,
        lifecycle: this.getLifecycleType(type),
        version: 1,
        created_at: object.createdAt,
        updated_at: object.updatedAt
      });
    }

    // Emit event
    eventBus.emit('object:created', object, 'local');

    // Queue for auto-embedding (fire-and-forget)
    autoEmbedQueue.enqueue(object.id);


    // Apply template layout if exists
    this.applyTemplateLayout(object.id, type).catch(err => {
      console.warn('Failed to apply template layout:', err);
      // Don't throw - object was created successfully
    });

    return object;
  }

  /**
   * Apply template layout to a newly created object
   * Creates blocks based on the template's contentLayout
   */
  private async applyTemplateLayout(objectId: string, type: PKMObject['type']): Promise<void> {
    try {
      // Get the template for this object type
      const template = await db.templates.where('objectType').equals(type).first();

      if (!template?.contentLayout?.length) {
        return; // No layout defined
      }

      console.log('[DataRepository] Applying template layout for:', type, 'with', template.contentLayout.length, 'blocks');

      // Create blocks from template layout
      for (const templateBlock of template.contentLayout) {
        const block: Block = {
          id: generateId(),
          objectId,
          type: templateBlock.type === 'property-field' ? 'text' : templateBlock.type,
          content: templateBlock.defaultContent || '',
          x: templateBlock.position.x,
          y: templateBlock.position.y,
          width: templateBlock.position.width,
          height: templateBlock.position.height,
          zIndex: templateBlock.position.zIndex,
          styles: templateBlock.styles,
          metadata: templateBlock.fieldId
            ? { boundToField: templateBlock.fieldId }
            : templateBlock.widgetConfig
              ? { widgetConfig: templateBlock.widgetConfig }
              : undefined,
          createdAt: now(),
          updatedAt: now(),
        };

        await db.blocks.add(block);

        // Emit block created event
        eventBus.emit('block:created', block, 'local');
      }
    } catch (error) {
      console.error('Error applying template layout:', error);
    }
  }


  /**
   * Get an object by ID
   * Falls back to Supabase if not found locally (for newly clipped objects)
   */
  async getObject(id: string): Promise<PKMObject | undefined> {
    // First, try local IndexedDB
    let obj = await db.objects.get(id);

    // If not found locally and we're online, try fetching from Supabase
    if (!obj && this.isOnline) {
      try {
        const { data: remoteObj, error } = await supabase
          .from('objects')
          .select('*')
          .eq('id', id)
          .single();

        if (remoteObj && !error) {
          // Convert snake_case to camelCase and save locally
          const localObj: PKMObject = {
            id: remoteObj.id,
            userId: remoteObj.user_id,
            name: remoteObj.name,
            type: remoteObj.type,
            content: remoteObj.content || '',
            color: remoteObj.color || undefined,
            tags: remoteObj.tags || [],
            status: remoteObj.status || 'active',
            distillation: remoteObj.distillation || 'raw',
            sourceType: remoteObj.source_type || undefined,
            createdAt: remoteObj.created_at,
            updatedAt: remoteObj.updated_at,
            deletedAt: remoteObj.deleted_at || undefined,
            version: remoteObj.version || 1,
          };

          // Save to local IndexedDB for future access
          await db.objects.put(localObj);
          obj = localObj;

          // Also fetch and save blocks for this object
          console.log('[DataRepository] Fetching blocks from Supabase for:', id);
          const { data: remoteBlocks } = await supabase
            .from('blocks')
            .select('*')
            .eq('object_id', id);

          console.log('[DataRepository] Found', remoteBlocks?.length || 0, 'blocks from Supabase');
          if (remoteBlocks && remoteBlocks.length > 0) {
            const localBlocks = remoteBlocks.map(b => ({
              id: b.id,
              objectId: b.object_id,
              userId: b.user_id,
              type: b.type,
              content: b.content || '',
              x: b.x || 0,
              y: b.y || 0,
              width: b.width || 400,
              height: b.height || 200,
              zIndex: b.z_index || 0,
              checked: b.checked || false,
              metadata: b.metadata ? (typeof b.metadata === 'string' ? JSON.parse(b.metadata) : b.metadata) : {},
              styles: b.styles ? (typeof b.styles === 'string' ? JSON.parse(b.styles) : b.styles) : {},
              createdAt: b.created_at,
              updatedAt: b.updated_at,
              version: b.version || 1,
            }));
            await db.blocks.bulkPut(localBlocks);
          }

          // Also fetch and save properties for this object
          const { data: remoteProps } = await supabase
            .from('properties')
            .select('*')
            .eq('object_id', id);

          if (remoteProps && remoteProps.length > 0) {
            const localProps = remoteProps.map(p => ({
              id: p.id,
              objectId: p.object_id,
              name: p.name,
              type: p.type,
              value: p.value,
              createdAt: p.created_at,
              updatedAt: p.updated_at,
            }));
            await db.properties.bulkPut(localProps);
          }

          console.log(`[DataRepository] Fetched object ${id} from Supabase and cached locally`);
        }
      } catch (error) {
        console.error('[DataRepository] Error fetching object from Supabase:', error);
      }
    }

    if (!obj) return undefined;

    // Load properties for this object
    const properties = await db.properties.where('objectId').equals(id).toArray();
    return { ...obj, properties };
  }

  /**
   * Update an object
   * Uses debounced sync to prevent flooding the sync queue during rapid typing
   */
  async updateObject(id: string, updates: Partial<PKMObject>): Promise<void> {
    const updatedData = {
      ...updates,
      updatedAt: now(),
    };

    // Update IndexedDB immediately (fast, local)
    await db.objects.update(id, updatedData);

    // Emit event immediately for UI responsiveness
    eventBus.emit('object:updated', { id, updates: updatedData }, 'local');

    // Accumulate which columns changed so the debounced flush can queue a delta
    // (only the changed columns) rather than the whole row.
    const dirty = this.objectDirtyColumns.get(id) ?? new Set<string>();
    for (const key of Object.keys(updates)) dirty.add(key);
    this.objectDirtyColumns.set(id, dirty);

    // Debounce the sync queue operation - only queue after user stops typing
    this.debouncedObjectSync(id, async () => {
      // Read fresh data from IndexedDB at sync time
      const object = await db.objects.get(id);
      const changed = this.objectDirtyColumns.get(id);
      this.objectDirtyColumns.delete(id);
      if (object && changed && changed.size > 0) {
        // Build a DELTA: only the whitelisted columns that actually changed,
        // read fresh from IndexedDB, plus updated_at (always). Concurrent edits
        // to other columns on another device are preserved (PATCH semantics).
        const delta: Record<string, any> = {};
        for (const key of changed) {
          if (!this.OBJECT_DELTA_COLUMNS.has(key)) continue;
          delta[key] = key === 'sourceType' ? (object.sourceType ?? null) : (object as any)[key];
        }
        delta.updated_at = object.updatedAt; // ALWAYS — julianday guard + server trigger depend on it

        // Skip if nothing whitelisted changed (only updated_at present).
        if (Object.keys(delta).length > 1) {
          await syncEngine.queueChange('objects', 'update', id, delta);

          // Queue for auto-embedding after sync
          autoEmbedQueue.enqueue(id);
        }
      }
    });
  }

  /**
   * Delete an object (soft delete)
   */
  async deleteObject(id: string): Promise<void> {
    console.log('[DataRepository.deleteObject] Starting delete for:', id);
    const deletedAt = now();

    try {
      // Update local IndexedDB (MUST update both deletedAt AND updatedAt for sync to work!)
      const updateResult = await db.objects.update(id, {
        deletedAt,
        updatedAt: deletedAt  // CRITICAL: Update this so sync queries catch the change
      });
      console.log('[DataRepository.deleteObject] IndexedDB update result:', updateResult);

      if (updateResult === 0) {
        console.warn('[DataRepository.deleteObject] No object found with id:', id);
      }

      // Emit event immediately so UI updates
      eventBus.emit('object:deleted', { id, deletedAt }, 'local');
      console.log('[DataRepository.deleteObject] Emitted delete event');

      // Push deletion to Supabase IMMEDIATELY (don't wait for sync cycle)
      // This ensures other devices see the deletion right away via Realtime
      restUpdate('objects', { deleted_at: deletedAt, updated_at: deletedAt }, { eq: { id } }, { returning: false })
        .then(({ error }) => {
          if (error) {
            console.error('[DataRepository.deleteObject] Immediate Supabase push failed, falling back to queue:', error);
            // Fallback: queue for next sync cycle
            syncEngine.queueChange('objects', 'update', id, { deleted_at: deletedAt });
          } else {
            console.log('[DataRepository.deleteObject] Pushed deletion to Supabase immediately');
          }
        })
        .catch(err => {
          console.error('[DataRepository.deleteObject] Immediate push error, falling back to queue:', err);
          syncEngine.queueChange('objects', 'update', id, { deleted_at: deletedAt });
        });

      console.log('[DataRepository.deleteObject] Delete completed successfully');
    } catch (error) {
      console.error('[DataRepository.deleteObject] Error:', error);
      throw error;
    }
  }

  /**
   * Get all active objects (not deleted)
   * SECURITY: Filters by current user ID to prevent data leakage between users
   */
  /**
   * Batch-load properties for a list of objects in a single query.
   */
  private async _attachProperties(objects: PKMObject[]): Promise<PKMObject[]> {
    if (objects.length === 0) return [];
    const objectIds = objects.map(obj => obj.id);
    const allProperties = await db.properties.where('objectId').anyOf(objectIds).toArray();
    const propsByObjectId = new Map<string, typeof allProperties>();
    for (const prop of allProperties) {
      const list = propsByObjectId.get(prop.objectId);
      if (list) list.push(prop);
      else propsByObjectId.set(prop.objectId, [prop]);
    }
    return objects.map(obj => ({ ...obj, properties: propsByObjectId.get(obj.id) || [] }));
  }

  /**
   * Get all objects, but only load properties for the specified types.
   * Much faster when only certain object types need their properties.
   */
  async getAllObjectsWithSelectiveProperties(typesNeedingProperties: string[]): Promise<PKMObject[]> {
    const userId = this.getUserIdFromLocalStorage();

    const objects = await db.objects
      .filter((obj) => {
        if (obj.deletedAt) return false;
        if (userId && obj.userId && obj.userId !== userId) return false;
        return true;
      })
      .reverse()
      .sortBy('updatedAt');

    // Only load properties for objects of the specified types
    const objectsNeedingProps = objects.filter(obj => typesNeedingProperties.includes(obj.type));
    const objectIds = objectsNeedingProps.map(obj => obj.id);
    const allProperties = objectIds.length > 0
      ? await db.properties.where('objectId').anyOf(objectIds).toArray()
      : [];

    const propsByObjectId = new Map<string, typeof allProperties>();
    for (const prop of allProperties) {
      const list = propsByObjectId.get(prop.objectId);
      if (list) list.push(prop);
      else propsByObjectId.set(prop.objectId, [prop]);
    }

    return objects.map(obj => ({
      ...obj,
      properties: propsByObjectId.get(obj.id) || [],
    }));
  }

  // Deduplicate concurrent getAllObjects calls — share a single in-flight promise
  private _getAllObjectsPromise: Promise<PKMObject[]> | null = null;

  async getAllObjects(): Promise<PKMObject[]> {
    // If a call is already in flight, piggyback on it
    if (this._getAllObjectsPromise) {
      return this._getAllObjectsPromise;
    }

    this._getAllObjectsPromise = this._getAllObjectsImpl();

    try {
      return await this._getAllObjectsPromise;
    } finally {
      this._getAllObjectsPromise = null;
    }
  }

  private async _getAllObjectsImpl(): Promise<PKMObject[]> {
    const userId = this.getUserIdFromLocalStorage();

    const objects = await db.objects
      .filter((obj) => {
        // Filter out deleted objects
        if (obj.deletedAt) return false;

        // SECURITY: Filter by userId if available
        if (userId && obj.userId && obj.userId !== userId) {
          return false;
        }

        return true;
      })
      .reverse()
      .sortBy('updatedAt');

    return this._attachProperties(objects);
  }

  /**
   * Get objects filtered by type directly from the database index.
   * Much faster than getAllObjects() + filter for large datasets.
   */
  async getObjectsByType(type: string): Promise<PKMObject[]> {
    const userId = this.getUserIdFromLocalStorage();

    const objects = await db.objects
      .where('type')
      .equals(type)
      .filter((obj) => {
        if (obj.deletedAt) return false;
        if (userId && obj.userId && obj.userId !== userId) return false;
        return true;
      })
      .toArray();

    return this._attachProperties(objects);
  }

  /**
   * Get top-level objects (not contained by any other object)
   */
  async getTopLevelObjects(): Promise<PKMObject[]> {
    const allObjects = await this.getAllObjects();
    const containedIds = new Set(
      (await db.links.where('linkType').equals('contains').toArray())
        .map(link => link.toObjectId)
    );
    return allObjects.filter(obj => !containedIds.has(obj.id));
  }

  /**
   * Get objects contained by a specific object
   * Uses IndexedDB links table (offline-first) instead of Supabase contexts
   */
  async getContainedObjects(objectId: string): Promise<PKMObject[]> {
    try {
      // Use IndexedDB links table instead of Supabase contexts (offline-first)
      const links = await db.links
        .where('fromObjectId')
        .equals(objectId)
        .and(link => link.linkType === 'contains')
        .toArray();

      if (links.length === 0) {
        return [];
      }

      // Get unique object IDs
      const objectIds = [...new Set(links.map(link => link.toObjectId))];

      // Fetch all objects from IndexedDB
      const objects = await Promise.all(
        objectIds.map(async (id) => {
          const obj = await db.objects.get(id);
          return obj && !obj.deletedAt ? obj : undefined;
        })
      );

      return objects.filter((obj): obj is PKMObject => obj !== undefined);
    } catch (err) {
      console.warn('[DataRepository] getContainedObjects failed:', err);
      return [];
    }
  }

  // ==================== CONTEXTS (NEW LINKING MODEL) ====================

  /**
   * Create a context link (new model)
   *
   * @param objectId - The object being linked
   * @param contextId - The context it belongs to
   * @param contextType - primary | appearance | reference
   * @param strength - strong | weak
   */
  async createContext(
    objectId: string,
    contextId: string,
    contextType: 'primary' | 'appearance' | 'reference',
    strength: 'strong' | 'weak' = 'weak',
    orderIndex?: number
  ): Promise<Context> {
    // OFFLINE-FIRST: Check local DB for duplicates first (never blocks on network)
    const existingLocal = await db.links
      .where('fromObjectId').equals(contextId)
      .and(link => link.toObjectId === objectId && link.linkType === (contextType === 'primary' ? 'contains' : 'references'))
      .first();

    if (existingLocal) {
      console.log('[createContext] Already exists locally, skipping');
      return {
        id: existingLocal.id,
        object_id: objectId,
        context_id: contextId,
        context_type: contextType,
        strength,
        order_index: orderIndex,
        created_at: existingLocal.createdAt
      };
    }

    // Create context + local link FIRST (offline-first, never fails)
    const context: Context = {
      id: generateId(),
      object_id: objectId,
      context_id: contextId,
      context_type: contextType,
      strength,
      order_index: orderIndex,
      created_at: now()
    };

    const link: Link = {
      id: context.id,
      fromObjectId: contextId,
      toObjectId: objectId,
      linkType: contextType === 'primary' ? 'contains' : 'references',
      createdAt: context.created_at
    };

    await db.links.add(link);
    console.log('[createContext] Local link created:', contextId, '→', objectId);

    // Queue for sync
    await syncEngine.queueChange('contexts', 'create', context.id, context);

    // Emit event
    eventBus.emit('context:created', context, 'local');

    // BACKGROUND: Ensure objects exist in Supabase (don't block, don't fail)
    this.ensureContextSyncedInBackground(objectId, contextId).catch(err => {
      console.warn('[createContext] Background sync failed (link still exists locally):', err);
    });

    return context;
  }

  /**
   * Background helper: ensure both objects in a context exist in Supabase
   * Non-blocking — local link is already created before this runs.
   */
  private async ensureContextSyncedInBackground(objectId: string, contextId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await this.ensureObjectSynced(objectId);
    await this.ensureObjectSynced(contextId);
  }

  /**
   * Ensure an object exists in Supabase, syncing it if necessary
   */
  private async ensureObjectSynced(objectId: string): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Check if object exists in Supabase
    const { data: supabaseObject } = await supabase
      .from('objects')
      .select('id')
      .eq('id', objectId)
      .maybeSingle();

    // If it exists, we're done
    if (supabaseObject) {
      return;
    }

    // Object doesn't exist in Supabase - we need to sync it now
    console.log(`📤 Object ${objectId} not in Supabase, syncing now...`);

    // Get the object from local DB
    const localObject = await db.objects.get(objectId);
    if (!localObject) {
      throw new Error(`Object ${objectId} not found in local database`);
    }

    // Insert directly into Supabase
    const { error } = await supabase.from('objects').insert({
      id: localObject.id,
      user_id: user.id,
      name: localObject.name,
      type: localObject.type,
      content: localObject.content || '',
      color: localObject.color,
      lifecycle: this.getLifecycleType(localObject.type),
      version: 1,
      created_at: localObject.createdAt,
      updated_at: localObject.updatedAt
    });

    if (error && error.code !== '23505') { // Ignore duplicate key errors
      throw error;
    }

    console.log(`✅ Object ${objectId} synced to Supabase`);
  }

  /**
   * Delete a context link
   */
  async deleteContext(contextId: string): Promise<void> {
    await db.links.delete(contextId);

    // Queue for sync
    await syncEngine.queueChange('contexts', 'delete', contextId, {});

    // Emit event
    eventBus.emit('context:deleted', { id: contextId }, 'local');
  }

  /**
   * Get all contexts for an object
   */
  async getContextsForObject(objectId: string): Promise<Link[]> {
    return await db.links
      .where('fromObjectId')
      .equals(objectId)
      .or('toObjectId')
      .equals(objectId)
      .toArray();
  }

  /**
   * Get an object's links resolved to the connected objects, split by direction
   * and carrying the link type so callers can badge each item (C = contains,
   * R = references). This is the shared source of truth for every links-display
   * view — most linking in the app is `references` (independent objects linked
   * to hubs); `contains` is reserved for genuinely nested things (blocks/images).
   *
   * - `out`: objects this object points to (fromObjectId === objectId)
   * - `in`:  objects that point to this object (toObjectId === objectId)
   */
  async getObjectLinks(objectId: string): Promise<{
    out: Array<{ object: PKMObject; linkType: Link['linkType']; linkId: string }>;
    in: Array<{ object: PKMObject; linkType: Link['linkType']; linkId: string }>;
  }> {
    const links = await this.getContextsForObject(objectId);

    const resolve = async (
      selfIsSource: boolean
    ): Promise<Array<{ object: PKMObject; linkType: Link['linkType']; linkId: string }>> => {
      const matching = links.filter(l =>
        selfIsSource ? l.fromObjectId === objectId : l.toObjectId === objectId
      );
      const resolved = await Promise.all(
        matching.map(async l => {
          const otherId = selfIsSource ? l.toObjectId : l.fromObjectId;
          const obj = await db.objects.get(otherId);
          return obj && !obj.deletedAt
            ? { object: obj, linkType: l.linkType, linkId: l.id }
            : null;
        })
      );
      return resolved.filter(
        (r): r is { object: PKMObject; linkType: Link['linkType']; linkId: string } => r !== null
      );
    };

    const [out, inbound] = await Promise.all([resolve(true), resolve(false)]);
    return { out, in: inbound };
  }

  // ==================== PROPERTIES ====================

  /**
   * Create a property
   */
  async createProperty(
    objectId: string,
    name: string,
    type: Property['type'],
    value: Property['value']
  ): Promise<Property> {
    // Get current user ID from localStorage (avoids hanging on supabase.auth.getUser())
    const userId = this.getUserIdFromLocalStorage();

    // Defensive: Ensure value is never null (Supabase constraint: NOT NULL)
    // For relation/rollup/formula properties, use empty array if value is null/undefined
    const safeValue = value ?? (type === 'relation' || type === 'rollup' || type === 'formula' ? [] : '');

    const property: Property = {
      id: generateId(),
      objectId,
      name,
      type,
      value: safeValue,
      createdAt: now(),
      updatedAt: now(),
    };

    await db.properties.add(property);

    // Only queue for sync if user is authenticated
    if (userId) {
      await syncEngine.queueChange('properties', 'create', property.id, {
        object_id: objectId,
        name,
        type,
        value: safeValue, // Use safe value here too
        user_id: userId,  // Required for RLS policy
        created_at: property.createdAt,
        updated_at: property.updatedAt
      });
    }

    // Emit event
    eventBus.emit('property:updated', { objectId, property }, 'local');

    return property;
  }

  /**
   * Get properties for an object
   */
  async getPropertiesForObject(objectId: string): Promise<Property[]> {
    return await db.properties.where('objectId').equals(objectId).toArray();
  }

  /**
   * Update a property
   */
  async updateProperty(id: string, updates: Partial<Property>): Promise<void> {
    // Get current user ID from localStorage (avoids hanging on supabase.auth.getUser())
    const userId = this.getUserIdFromLocalStorage();

    // First, read the current property to check for null values
    const currentProperty = await db.properties.get(id);

    // Defensive: Fix null values in IndexedDB before updating
    // This auto-repairs legacy data that has null values
    if (currentProperty && (currentProperty.value === null || currentProperty.value === undefined)) {
      console.warn(`[DataRepository] Auto-fixing null value in property ${id} (${currentProperty.name})`);
      const defaultValue =
        currentProperty.type === 'relation' ||
        currentProperty.type === 'multi-relation' ||
        currentProperty.type === 'rollup' ||
        currentProperty.type === 'formula'
          ? []
          : currentProperty.type === 'checkbox'
          ? false
          : currentProperty.type === 'number'
          ? 0
          : currentProperty.type === 'multi_select'
          ? []
          : '';

      updates = { ...updates, value: defaultValue };
    }

    const updatedData = {
      ...updates,
      updatedAt: now(),
    };

    await db.properties.update(id, updatedData);

    // Get objectId for event
    const property = await db.properties.get(id);
    if (property) {
      // Only queue for sync if user is authenticated
      if (userId) {
        // Defensive: Ensure value is never null (Supabase constraint: NOT NULL)
        // This protects against existing properties with null values from before the fix
        const safeValue = property.value ?? (
          property.type === 'relation' ||
          property.type === 'multi-relation' ||
          property.type === 'rollup' ||
          property.type === 'formula'
            ? []
            : ''
        );

        await syncEngine.queueChange('properties', 'update', id, {
          object_id: property.objectId,
          name: property.name,
          type: property.type,
          value: safeValue, // ✅ Use safe value instead of raw property.value
          user_id: userId,  // Required for RLS policy
          updated_at: updatedData.updatedAt
        });
      }

      // Emit event
      eventBus.emit('property:updated', { objectId: property.objectId, property }, 'local');
    }
  }

  /**
   * Delete a property
   */
  async deleteProperty(id: string): Promise<void> {
    const property = await db.properties.get(id);

    await db.properties.delete(id);

    if (property) {
      // Queue for sync
      await syncEngine.queueChange('properties', 'delete', id, {});

      // Emit event
      eventBus.emit('property:updated', { objectId: property.objectId }, 'local');
    }
  }

  // ==================== ATTACHMENTS ====================

  /**
   * Create an attachment
   */
  async createAttachment(
    objectId: string,
    name: string,
    type: Attachment['type'],
    data: {
      filePath?: string;
      fileSize?: number;
      mimeType?: string;
      url?: string;
      title?: string;
      description?: string;
      faviconUrl?: string;
    }
  ): Promise<Attachment> {
    const attachment: Attachment = {
      id: generateId(),
      objectId,
      name,
      type,
      ...data,
      createdAt: now(),
      updatedAt: now(),
    };

    await db.attachments.add(attachment);

    // Queue for sync
    await syncEngine.queueChange('attachments', 'create', attachment.id, {
      object_id: objectId,
      name,
      type,
      file_path: data.filePath,
      file_size: data.fileSize,
      mime_type: data.mimeType,
      url: data.url,
      title: data.title,
      description: data.description,
      favicon_url: data.faviconUrl,
      created_at: attachment.createdAt,
      updated_at: attachment.updatedAt
    });

    return attachment;
  }

  /**
   * Get attachments for an object
   */
  async getAttachmentsForObject(objectId: string): Promise<Attachment[]> {
    return await db.attachments.where('objectId').equals(objectId).toArray();
  }

  /**
   * Delete an attachment
   */
  async deleteAttachment(id: string): Promise<void> {
    await db.attachments.delete(id);

    // Queue for sync
    await syncEngine.queueChange('attachments', 'delete', id, {});
  }

  // ==================== TEMPLATES ====================

  /**
   * Get template for an object type
   */
  async getTemplate(objectType: ObjectTemplate['objectType']): Promise<ObjectTemplate | undefined> {
    return await db.templates.where('objectType').equals(objectType).first();
  }

  /**
   * Get all templates
   */
  async getAllTemplates(): Promise<ObjectTemplate[]> {
    return await db.templates.toArray();
  }

  // ==================== TAGS ====================

  /**
   * Add a tag to an object
   */
  async addTag(objectId: string, tag: string): Promise<void> {
    const normalizedTag = tag.trim().toLowerCase();
    if (!normalizedTag) return;

    const object = await db.objects.get(objectId);
    if (!object) return;

    const currentTags = object.tags || [];
    if (currentTags.includes(normalizedTag)) return; // Already has tag

    const newTags = [...currentTags, normalizedTag];
    await this.updateObject(objectId, { tags: newTags });
  }

  /**
   * Remove a tag from an object
   */
  async removeTag(objectId: string, tag: string): Promise<void> {
    const normalizedTag = tag.trim().toLowerCase();
    const object = await db.objects.get(objectId);
    if (!object || !object.tags) return;

    const newTags = object.tags.filter(t => t !== normalizedTag);
    await this.updateObject(objectId, { tags: newTags });
  }

  /**
   * Set all tags for an object (replaces existing)
   */
  async setTags(objectId: string, tags: string[]): Promise<void> {
    const normalizedTags = tags
      .map(t => t.trim().toLowerCase())
      .filter(t => t.length > 0);
    // Remove duplicates
    const uniqueTags = [...new Set(normalizedTags)];
    await this.updateObject(objectId, { tags: uniqueTags });
  }

  /**
   * Get all unique tags across all objects (for autocomplete)
   * SECURITY: Filters by current user ID to prevent data leakage between users
   */
  async getAllTags(): Promise<string[]> {
    const userId = this.getUserIdFromLocalStorage();

    const objects = await db.objects
      .filter(obj => {
        // Filter out deleted objects
        if (obj.deletedAt) return false;

        // Must have tags
        if (!obj.tags || obj.tags.length === 0) return false;

        // SECURITY: Filter by userId
        if (userId && obj.userId && obj.userId !== userId) {
          return false;
        }

        return true;
      })
      .toArray();

    const tagSet = new Set<string>();
    for (const obj of objects) {
      if (obj.tags) {
        obj.tags.forEach(tag => tagSet.add(tag));
      }
    }

    return Array.from(tagSet).sort();
  }

  /**
   * Get objects by tag
   * SECURITY: Filters by current user ID to prevent data leakage between users
   */
  async getObjectsByTag(tag: string): Promise<PKMObject[]> {
    const userId = this.getUserIdFromLocalStorage();
    const normalizedTag = tag.trim().toLowerCase();

    const objects = await db.objects
      .filter(obj => {
        // Filter out deleted objects
        if (obj.deletedAt) return false;

        // Check tag match
        if (!obj.tags?.includes(normalizedTag)) return false;

        // SECURITY: Filter by userId
        if (userId && obj.userId && obj.userId !== userId) {
          return false;
        }

        return true;
      })
      .toArray();

    return this._attachProperties(objects);
  }

  /**
   * Get objects matching any of the given tags
   * SECURITY: Filters by current user ID to prevent data leakage between users
   */
  async getObjectsByTags(tags: string[]): Promise<PKMObject[]> {
    const userId = this.getUserIdFromLocalStorage();
    const normalizedTags = tags.map(t => t.trim().toLowerCase());

    const objects = await db.objects
      .filter(obj => {
        // Filter out deleted objects
        if (obj.deletedAt) return false;

        // Check tag match
        if (!obj.tags?.some(t => normalizedTags.includes(t))) return false;

        // SECURITY: Filter by userId
        if (userId && obj.userId && obj.userId !== userId) {
          return false;
        }

        return true;
      })
      .toArray();

    return this._attachProperties(objects);
  }

  // ==================== STATUS & DISTILLATION ====================

  /**
   * Set the status (lifecycle stage) of an object
   */
  async setStatus(objectId: string, status: ObjectStatus): Promise<void> {
    await this.updateObject(objectId, { status });
  }

  /**
   * Set the distillation level (Progressive Summarization) of an object
   */
  async setDistillation(objectId: string, level: DistillationLevel): Promise<void> {
    await this.updateObject(objectId, { distillation: level });
  }

  /**
   * Get all objects with a specific status
   * SECURITY: Filters by current user ID to prevent data leakage between users
   */
  async getObjectsByStatus(status: ObjectStatus): Promise<PKMObject[]> {
    const userId = this.getUserIdFromLocalStorage();

    const objects = await db.objects
      .filter(obj => {
        // Filter out deleted objects
        if (obj.deletedAt) return false;

        // Check status match
        if (obj.status !== status) return false;

        // SECURITY: Filter by userId
        if (userId && obj.userId && obj.userId !== userId) {
          return false;
        }

        return true;
      })
      .toArray();

    return this._attachProperties(objects);
  }

  /**
   * Get all archived objects (status = 'archived')
   */
  async getArchivedObjects(): Promise<PKMObject[]> {
    return this.getObjectsByStatus('archived');
  }

  /**
   * Get objects by distillation level
   * SECURITY: Filters by current user ID to prevent data leakage between users
   */
  async getObjectsByDistillation(level: DistillationLevel): Promise<PKMObject[]> {
    const userId = this.getUserIdFromLocalStorage();

    const objects = await db.objects
      .filter(obj => {
        // Filter out deleted objects
        if (obj.deletedAt) return false;

        // Check distillation match
        if (obj.distillation !== level) return false;

        // SECURITY: Filter by userId
        if (userId && obj.userId && obj.userId !== userId) {
          return false;
        }

        return true;
      })
      .toArray();

    return this._attachProperties(objects);
  }

  // ==================== HELPERS ====================

  /**
   * Determine lifecycle type based on object type
   */
  private getLifecycleType(type: PKMObject['type']): 'ephemeral' | 'persistent' {
    const ephemeralTypes = ['task', 'meeting'];
    return ephemeralTypes.includes(type) ? 'ephemeral' : 'persistent';
  }

  /**
   * Add an object to a container (creates containment link)
   */
  async addObjectToContainer(containerId: string, objectId: string): Promise<void> {
    await this.createContext(objectId, containerId, 'primary', 'strong');
  }

  /**
   * Check if online
   */
  isOnlineNow(): boolean {
    return this.isOnline;
  }

  // ==================== BLOCKS ====================

  /**
   * Create a new block
   * @param block - Block data. If id is provided, it will be used; otherwise a new ID is generated.
   */
  async createBlock(block: Omit<Block, 'createdAt' | 'updatedAt'> & { id?: string }): Promise<Block> {
    // Get current user ID from localStorage (avoids hanging on supabase.auth.getUser())
    const userId = this.getUserIdFromLocalStorage();

    const newBlock: Block = {
      ...block,
      id: block.id || generateId(),
      createdAt: now(),
      updatedAt: now(),
    };

    // Save to IndexedDB first (offline-first, never hangs)
    await db.blocks.add(newBlock);
    console.log('[DataRepository.createBlock] Saved block to IndexedDB:', newBlock.id);

    // Only queue for sync if user is authenticated
    if (userId) {
      await syncEngine.queueChange('blocks', 'create', newBlock.id, {
        object_id: newBlock.objectId,
        type: newBlock.type,
        content: newBlock.content,
        x: newBlock.x,
        y: newBlock.y,
        width: newBlock.width,
        height: newBlock.height,
        z_index: newBlock.zIndex,
        metadata: newBlock.metadata || {},
        styles: newBlock.styles || null,
        title: newBlock.title ?? null,
        icon: newBlock.icon || null,
        is_linkable: newBlock.isLinkable || false,
        name: newBlock.name || null,
        user_id: userId,  // Required for RLS policy
        created_at: newBlock.createdAt,
        updated_at: newBlock.updatedAt,
      });
    }

    eventBus.emit('block:created', newBlock, 'local');
    return newBlock;
  }

  /**
   * Get blocks for an object
   * Falls back to Supabase if no blocks found locally (for newly clipped objects)
   */
  async getBlocksForObject(objectId: string): Promise<Block[]> {
    const allLocalBlocks = await db.blocks.where('objectId').equals(objectId).sortBy('zIndex');
    // Filter out soft-deleted blocks
    let blocks = allLocalBlocks.filter(b => !b.deletedAt);

    // Only fall back to Supabase if we have NO local records at all (including soft-deleted).
    // If a soft-deleted block exists locally but hasn't synced yet, refetching would
    // overwrite our pending delete with the pre-delete remote version and the block
    // would reappear.
    if (allLocalBlocks.length === 0 && this.isOnline) {
      try {
        console.log('[DataRepository] No local blocks, fetching from Supabase via REST...');
        // Use direct REST API to avoid Supabase client hanging issues
        const { data: remoteBlocks, error } = await restSelect('blocks', {
          select: '*',
          eq: { object_id: objectId },
          order: 'z_index.asc',
          is: { deleted_at: null } // Filter out soft-deleted blocks
        });

        if (remoteBlocks && remoteBlocks.length > 0 && !error) {
          console.log('[DataRepository] Found', remoteBlocks.length, 'blocks in Supabase');
          // Convert snake_case to camelCase and save locally
          const localBlocks: Block[] = remoteBlocks.map(block => ({
            id: block.id,
            objectId: block.object_id,
            type: block.type,
            content: block.content || '',
            x: block.x,
            y: block.y,
            width: block.width,
            height: block.height,
            zIndex: block.z_index,
            metadata: block.metadata ? (typeof block.metadata === 'string' ? JSON.parse(block.metadata) : block.metadata) : {},
            styles: block.styles ? (typeof block.styles === 'string' ? JSON.parse(block.styles) : block.styles) : undefined,
            isLinkable: block.is_linkable || false,
            name: block.name || undefined,
            createdAt: block.created_at,
            updatedAt: block.updated_at,
            deletedAt: block.deleted_at || undefined,
          }));

          // Save to IndexedDB for future access
          await db.blocks.bulkPut(localBlocks);
          console.log('[DataRepository] Saved', localBlocks.length, 'blocks to IndexedDB');
          blocks = localBlocks;
        }
      } catch (err) {
        console.warn('[DataRepository] Failed to fetch blocks from Supabase:', err);
      }
    }

    return blocks;
  }

  /**
   * Update a block
   * Uses debounced sync to prevent flooding the sync queue during rapid typing
   */
  async updateBlock(id: string, updates: Partial<Block>): Promise<void> {
    // Don't allow objectId to be overwritten with empty value
    // This can happen when CanvasEditor passes blocks with objectId: ''
    const safeUpdates = { ...updates };
    if (!safeUpdates.objectId) {
      delete safeUpdates.objectId;
    }

    // Update IndexedDB immediately (fast, local)
    await db.blocks.update(id, {
      ...safeUpdates,
      updatedAt: now(),
    });

    // Read block for event emission
    const block = await db.blocks.get(id);
    if (block) {
      // Emit event immediately for UI responsiveness
      eventBus.emit('block:updated', block, 'local');
    }

    // Debounce the sync queue operation - only queue after user stops typing
    this.debouncedBlockSync(id, async () => {
      // Get current user ID from localStorage (avoids hanging)
      const userId = this.getUserIdFromLocalStorage();
      if (!userId) return;

      // Read fresh data from IndexedDB at sync time
      const freshBlock = await db.blocks.get(id);
      if (freshBlock) {
        console.log('[DataRepository.updateBlock] Debounced sync - queueing block:', id, 'content length:', freshBlock.content?.length || 0);

        // Encode Yjs CRDT state for text blocks to enable conflict-free merge
        // Only encode for text/note blocks; other types (image, widget, etc.) skip CRDT
        let crdtState: string | null = null;
        const TEXT_CRDT_TYPES = ['text', 'heading', 'heading1', 'heading2', 'heading3', 'list', 'checklist', 'tasklist', 'code', 'callout'];
        console.log(`[DataRepository.updateBlock] Block type: "${freshBlock.type}", hasContent: ${!!freshBlock.content}, isEncrypted: ${!!freshBlock.isEncrypted}, eligible for CRDT: ${TEXT_CRDT_TYPES.includes(freshBlock.type)}`);
        if (TEXT_CRDT_TYPES.includes(freshBlock.type) && freshBlock.content && !freshBlock.isEncrypted) {
          try {
            // Pre-warm the Y.Doc from stored state before encoding new content.
            // This ensures the CRDT vector clock is preserved across app restarts.
            if (freshBlock.crdtState) {
              initYDocFromState(freshBlock.id, freshBlock.crdtState);
            }
            crdtState = setBlockContent(freshBlock.id, freshBlock.content);
            // Persist crdtState back to IndexedDB for next session recovery
            const updateCount = await db.blocks.update(id, { crdtState });
            // Verify the write: re-read to confirm crdtState is stored
            const verifyBlock = await db.blocks.get(id);
            console.log(`[DataRepository.updateBlock] CRDT encoded: length=${crdtState?.length || 0}, IDB updateCount=${updateCount}, IDB verify crdtState length=${verifyBlock?.crdtState?.length || 0}`);
          } catch (err) {
            console.warn('[DataRepository.updateBlock] Failed to encode Yjs state:', err);
          }
        }

        await syncEngine.queueChange('blocks', 'update', id, {
          object_id: freshBlock.objectId,
          type: freshBlock.type,
          content: freshBlock.content,
          x: freshBlock.x,
          y: freshBlock.y,
          width: freshBlock.width,
          height: freshBlock.height,
          z_index: freshBlock.zIndex,
          metadata: freshBlock.metadata || {},
          styles: freshBlock.styles || null,
          title: freshBlock.title ?? null,
          icon: freshBlock.icon || null,
          is_linkable: freshBlock.isLinkable || false,
          name: freshBlock.name || null,
          user_id: userId,
          updated_at: freshBlock.updatedAt,
          crdt_state: crdtState,
        });

        // Auto-sync relation properties from block content
        // This creates/updates "Tasks", "Linked Objects", and "Backlinks" properties
        await autoRelationService.syncObjectRelations(freshBlock.objectId);

        // Queue for auto-embedding after block content changes
        autoEmbedQueue.enqueue(freshBlock.objectId);
      }
    });
  }

  /**
   * Delete a block (soft delete - marks as deleted)
   */
  async deleteBlock(id: string): Promise<void> {
    const block = await db.blocks.get(id);
    if (!block) return;

    // Soft delete: mark with deletedAt timestamp
    const deletedBlock = {
      ...block,
      deletedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    await db.blocks.put(deletedBlock);
    await syncEngine.queueChange('blocks', 'update', id, deletedBlock);
    eventBus.emit('block:deleted', { id }, 'local');
  }

  /**
   * Delete all blocks for an object
   */
  async deleteBlocksForObject(objectId: string): Promise<void> {
    const blocks = await this.getBlocksForObject(objectId);
    for (const block of blocks) {
      await this.deleteBlock(block.id);
    }
  }

  // ==================== USER COLLECTIONS ====================

  /**
   * Create a new user collection
   */
  async createUserCollection(
    name: string,
    icon: string = '📁',
    description?: string,
    color?: string,
    pinned: boolean = false
  ): Promise<UserCollection> {
    const userId = this.getUserIdFromLocalStorage();

    const collection: UserCollection = {
      id: generateId(),
      name,
      description,
      icon,
      color,
      pinned,
      sortOrder: 0,
      userId: userId || undefined,
      createdAt: now(),
      updatedAt: now(),
    };

    await db.userCollections.add(collection);

    // Queue for sync if authenticated
    if (userId) {
      await syncEngine.queueChange('user_collections', 'create', collection.id, {
        name: collection.name,
        description: collection.description,
        icon: collection.icon,
        color: collection.color,
        pinned: collection.pinned,
        sort_order: collection.sortOrder,
        user_id: userId,
        created_at: collection.createdAt,
        updated_at: collection.updatedAt,
      });
    }

    eventBus.emit('collection:created', collection, 'local');
    return collection;
  }

  /**
   * Get a user collection by ID
   */
  async getUserCollection(id: string): Promise<UserCollection | undefined> {
    return await db.userCollections.get(id);
  }

  /**
   * Get all user collections
   */
  async getAllUserCollections(): Promise<UserCollection[]> {
    return await db.userCollections.toArray();
  }

  /**
   * Get pinned user collections for Quick Access sidebar
   */
  async getPinnedUserCollections(): Promise<UserCollection[]> {
    const all = await db.userCollections.toArray();
    return all.filter(c => c.pinned).sort((a, b) => a.sortOrder - b.sortOrder);
  }

  /**
   * Update a user collection
   */
  async updateUserCollection(id: string, updates: Partial<UserCollection>): Promise<void> {
    const { data: { user } } = await supabase.auth.getUser();

    const updatedData = {
      ...updates,
      updatedAt: now(),
    };

    await db.userCollections.update(id, updatedData);

    // Queue for sync if authenticated
    if (user) {
      const collection = await db.userCollections.get(id);
      if (collection) {
        await syncEngine.queueChange('user_collections', 'update', id, {
          name: collection.name,
          description: collection.description,
          icon: collection.icon,
          color: collection.color,
          pinned: collection.pinned,
          sort_order: collection.sortOrder,
          user_id: user.id,
          updated_at: collection.updatedAt,
        });
      }
    }

    eventBus.emit('collection:updated', { id, updates: updatedData }, 'local');
  }

  /**
   * Delete a user collection
   */
  async deleteUserCollection(id: string): Promise<void> {
    // Delete all items in this collection first
    await db.userCollectionItems.where('collectionId').equals(id).delete();

    // Delete the collection
    await db.userCollections.delete(id);

    // Queue for sync
    await syncEngine.queueChange('user_collections', 'delete', id, {});

    eventBus.emit('collection:deleted', { id }, 'local');
  }

  /**
   * Add an object to a user collection
   */
  async addObjectToUserCollection(
    collectionId: string,
    objectId: string,
    note?: string
  ): Promise<UserCollectionItem> {
    const { data: { user } } = await supabase.auth.getUser();

    const item: UserCollectionItem = {
      collectionId,
      objectId,
      addedAt: now(),
      note,
    };

    await db.userCollectionItems.put(item);

    // Queue for sync if authenticated
    if (user) {
      await syncEngine.queueChange('user_collection_items', 'create', `${collectionId}-${objectId}`, {
        collection_id: collectionId,
        object_id: objectId,
        added_at: item.addedAt,
        note: item.note,
      });
    }

    eventBus.emit('collection:item:added', { collectionId, objectId }, 'local');
    return item;
  }

  /**
   * Remove an object from a user collection
   */
  async removeObjectFromUserCollection(collectionId: string, objectId: string): Promise<void> {
    await db.userCollectionItems.delete([collectionId, objectId]);

    // Queue for sync
    await syncEngine.queueChange('user_collection_items', 'delete', `${collectionId}-${objectId}`, {});

    eventBus.emit('collection:item:removed', { collectionId, objectId }, 'local');
  }

  /**
   * Get all object IDs in a collection
   */
  async getObjectsInCollection(collectionId: string): Promise<string[]> {
    const items = await db.userCollectionItems
      .where('collectionId')
      .equals(collectionId)
      .toArray();
    return items.map(item => item.objectId);
  }

  /**
   * Get all collections an object belongs to
   */
  async getCollectionsForObject(objectId: string): Promise<UserCollection[]> {
    const items = await db.userCollectionItems
      .where('objectId')
      .equals(objectId)
      .toArray();

    const collectionIds = items.map(item => item.collectionId);
    const collections = await Promise.all(
      collectionIds.map(id => this.getUserCollection(id))
    );

    return collections.filter((c): c is UserCollection => c !== undefined);
  }

  /**
   * Check if an object is in a collection
   */
  async isObjectInCollection(collectionId: string, objectId: string): Promise<boolean> {
    const item = await db.userCollectionItems.get([collectionId, objectId]);
    return item !== undefined;
  }

  /**
   * Get the count of items in a collection
   */
  async getCollectionItemCount(collectionId: string): Promise<number> {
    return await db.userCollectionItems
      .where('collectionId')
      .equals(collectionId)
      .count();
  }

  /**
   * Get full objects in a collection (with properties)
   * SECURITY: Filters by current user ID to prevent data leakage between users
   */
  async getCollectionObjects(collectionId: string): Promise<PKMObject[]> {
    const userId = this.getUserIdFromLocalStorage();
    const objectIds = await this.getObjectsInCollection(collectionId);
    const objects = await Promise.all(objectIds.map(id => this.getObject(id)));

    return objects.filter((obj): obj is PKMObject => {
      // Filter out undefined and deleted objects
      if (!obj || obj.deletedAt) return false;

      // SECURITY: Filter by userId
      if (userId && obj.userId && obj.userId !== userId) {
        return false;
      }

      return true;
    });
  }

  // ============================================================================
  // ADVANCED PROPERTIES - RELATIONS
  // ============================================================================

  /**
   * Create a new relation between objects
   * Queues change for sync and emits events
   */
  async createRelation(
    propertyId: string,
    sourceObjectId: string,
    targetObjectId: string
  ): Promise<PropertyRelation> {
    const relation = await propertyRelationService.createRelation(
      propertyId,
      sourceObjectId,
      targetObjectId
    );

    // Queue for sync
    await syncEngine.queueChange('property_relations', 'create', relation.id, {
      property_id: relation.property_id,
      source_object_id: relation.source_object_id,
      target_object_id: relation.target_object_id,
      position: relation.position,
      created_at: relation.created_at,
    });

    // Emit event
    eventBus.emit('relation:created', { propertyId, sourceObjectId, targetObjectId }, 'local');

    return relation;
  }

  /**
   * Delete a relation
   */
  async deleteRelation(relationId: string): Promise<void> {
    await propertyRelationService.deleteRelation(relationId);

    // Queue for sync
    await syncEngine.queueChange('property_relations', 'delete', relationId, {});

    // Emit event
    eventBus.emit('relation:deleted', { relationId }, 'local');
  }

  /**
   * Get related objects for a property
   */
  async getRelatedObjects(
    propertyId: string,
    objectId: string
  ): Promise<PKMObject[]> {
    return await propertyRelationService.getRelatedObjects(propertyId, objectId);
  }

  /**
   * Get backlinks - objects that link to this object
   */
  async getBacklinks(
    propertyId: string,
    targetObjectId: string
  ): Promise<Array<{ sourceObject: PKMObject; relation: PropertyRelation }>> {
    return await propertyRelationService.getBacklinks(propertyId, targetObjectId);
  }

  /**
   * Update positions of relations (for drag-and-drop reordering)
   */
  async updateRelationPositions(
    propertyId: string,
    objectId: string,
    orderedRelationIds: string[]
  ): Promise<void> {
    await propertyRelationService.updateRelationPositions(propertyId, objectId, orderedRelationIds);

    // Queue for sync
    orderedRelationIds.forEach(async (relationId, index) => {
      await syncEngine.queueChange('property_relations', 'update', relationId, {
        position: index,
      });
    });

    // Emit event
    eventBus.emit('relation:reordered', { propertyId, objectId }, 'local');
  }

  // ============================================================================
  // ADVANCED PROPERTIES - ROLLUPS
  // ============================================================================

  /**
   * Compute rollup value for a property
   * Returns the aggregated value from related objects
   */
  async computeRollup(
    propertyId: string,
    objectId: string
  ): Promise<number | string | null> {
    const result = await propertyRollupService.computeRollup(propertyId, objectId);
    return result.value;
  }

  /**
   * Create rollup configuration
   */
  async createRollup(config: CreateRollupRequest): Promise<PropertyRollup> {
    const rollup = await propertyRollupService.createRollup(config);

    // Queue for sync
    await syncEngine.queueChange('property_rollups', 'create', rollup.id, {
      property_id: rollup.property_id,
      relation_property_id: rollup.relation_property_id,
      target_property_name: rollup.target_property_name,
      aggregation_function: rollup.aggregation_function,
      filter_conditions: rollup.filter_conditions,
      created_at: rollup.created_at,
      updated_at: rollup.updated_at,
    });

    return rollup;
  }

  /**
   * Update rollup configuration
   */
  async updateRollup(
    rollupId: string,
    updates: Partial<PropertyRollup>
  ): Promise<void> {
    await propertyRollupService.updateRollup(rollupId, updates);

    // Queue for sync
    await syncEngine.queueChange('property_rollups', 'update', rollupId, {
      ...updates,
      updated_at: new Date().toISOString(),
    });

    // Emit event
    eventBus.emit('rollup:updated', { rollupId }, 'local');
  }

  /**
   * Delete rollup configuration
   */
  async deleteRollup(rollupId: string): Promise<void> {
    await propertyRollupService.deleteRollup(rollupId);

    // Queue for sync
    await syncEngine.queueChange('property_rollups', 'delete', rollupId, {});

    // Emit event
    eventBus.emit('rollup:deleted', { rollupId }, 'local');
  }

  // ============================================================================
  // ADVANCED PROPERTIES - FORMULAS
  // ============================================================================

  /**
   * Compute formula value for a property
   * Returns the evaluated result
   */
  async computeFormula(
    propertyId: string,
    objectId: string
  ): Promise<number | string | boolean | null> {
    const result = await propertyFormulaService.computeFormula(propertyId, objectId);
    return result.value;
  }

  /**
   * Create formula configuration
   */
  async createFormula(config: CreateFormulaRequest): Promise<PropertyFormula> {
    const formula = await propertyFormulaService.createFormula(config);

    // Queue for sync
    await syncEngine.queueChange('property_formulas', 'create', formula.id, {
      property_id: formula.property_id,
      expression: formula.expression,
      result_type: formula.result_type,
      dependencies: formula.dependencies,
      created_at: formula.created_at,
      updated_at: formula.updated_at,
    });

    return formula;
  }

  /**
   * Update formula configuration
   */
  async updateFormula(
    formulaId: string,
    updates: Partial<PropertyFormula>
  ): Promise<void> {
    await propertyFormulaService.updateFormula(formulaId, updates);

    // Queue for sync
    await syncEngine.queueChange('property_formulas', 'update', formulaId, {
      ...updates,
      updated_at: new Date().toISOString(),
    });

    // Emit event
    eventBus.emit('formula:updated', { formulaId }, 'local');
  }

  /**
   * Delete formula configuration
   */
  async deleteFormula(formulaId: string): Promise<void> {
    await propertyFormulaService.deleteFormula(formulaId);

    // Queue for sync
    await syncEngine.queueChange('property_formulas', 'delete', formulaId, {});

    // Emit event
    eventBus.emit('formula:deleted', { formulaId }, 'local');
  }

  /**
   * Validate formula expression before saving
   * Returns validation result with errors if any
   */
  async validateFormula(expression: string, propertyId?: string): Promise<{
    valid: boolean;
    errors: string[];
    dependencies: string[];
    hasCircularDependency: boolean;
  }> {
    return await propertyFormulaService.validateFormula(expression, propertyId);
  }

  // ============================================================================
  // ADVANCED PROPERTIES - OPTIONS (for Select/Multi-Select)
  // ============================================================================

  /**
   * Create a new option for select/multi-select property
   */
  async createPropertyOption(config: CreateOptionRequest): Promise<PropertyOption> {
    const option = await propertyOptionService.createOption(config);

    // Queue for sync
    await syncEngine.queueChange('property_options', 'create', option.id, {
      property_id: option.property_id,
      value: option.value,
      color: option.color,
      position: option.position,
      created_at: option.created_at,
      updated_at: option.updated_at,
      usage_count: option.usage_count,
    });

    // Emit event
    eventBus.emit('option:created', { propertyId: option.property_id, optionId: option.id }, 'local');

    return option;
  }

  /**
   * Update an option
   */
  async updatePropertyOption(
    optionId: string,
    updates: Partial<PropertyOption>
  ): Promise<void> {
    await propertyOptionService.updateOption(optionId, updates);

    // Queue for sync
    await syncEngine.queueChange('property_options', 'update', optionId, {
      ...updates,
      updated_at: new Date().toISOString(),
    });

    // Emit event
    eventBus.emit('option:updated', { optionId }, 'local');
  }

  /**
   * Delete an option
   */
  async deletePropertyOption(optionId: string): Promise<void> {
    await propertyOptionService.deleteOption(optionId);

    // Queue for sync
    await syncEngine.queueChange('property_options', 'delete', optionId, {});

    // Emit event
    eventBus.emit('option:deleted', { optionId }, 'local');
  }

  /**
   * Get all options for a property
   */
  async getPropertyOptions(propertyId: string): Promise<PropertyOption[]> {
    return await propertyOptionService.getOptions(propertyId);
  }

  /**
   * Reorder options (for drag-and-drop)
   */
  async reorderPropertyOptions(propertyId: string, optionIds: string[]): Promise<void> {
    await propertyOptionService.reorderOptions(propertyId, optionIds);

    // Queue for sync
    optionIds.forEach(async (optionId, index) => {
      await syncEngine.queueChange('property_options', 'update', optionId, {
        position: index,
      });
    });

    // Emit event
    eventBus.emit('option:reordered', { propertyId }, 'local');
  }

  // ============================================================================
  // ADVANCED PROPERTIES - COMPUTED CACHE
  // ============================================================================

  /**
   * Invalidate computed cache when property value changes
   * This triggers recalculation of dependent formulas and rollups
   */
  async invalidateComputedCache(propertyId: string, objectId: string): Promise<void> {
    await propertyComputedCacheService.invalidateCache(propertyId, objectId);

    // Emit event for UI updates
    eventBus.emit('cache:invalidated', { propertyId, objectId }, 'local');
  }

  /**
   * Get cached computed value
   */
  async getCachedValue(
    propertyId: string,
    objectId: string
  ): Promise<ComputedCacheEntry | null> {
    return await propertyComputedCacheService.getCachedValue(propertyId, objectId);
  }

  /**
   * Get cache statistics
   * Returns counts of total, stale, and fresh cache entries
   */
  async getCacheStats(): Promise<{
    total: number;
    stale: number;
    fresh: number;
    stalePercentage: number;
  }> {
    return await propertyComputedCacheService.getCacheStats();
  }

  /**
   * Recalculate all stale cache entries
   * Useful for background batch processing
   */
  async recalculateStaleCache(): Promise<{
    computed: number;
    skipped: number;
    failed: number;
  }> {
    return await propertyComputedCacheService.recalculateStaleCache();
  }

  // ============================================================================
  // Block Reminders
  // ============================================================================

  async createReminder(
    blockId: string,
    objectId: string,
    reminderAt: string,
    note?: string
  ): Promise<BlockReminder> {
    const userId = this.getUserIdFromLocalStorage();

    const reminder: BlockReminder = {
      id: generateId(),
      blockId,
      objectId,
      reminderAt,
      note,
      status: 'pending',
      createdAt: now(),
      updatedAt: now(),
    };

    await db.reminders.add(reminder);

    if (userId) {
      await syncEngine.queueChange('block_reminders', 'create', reminder.id, {
        block_id: blockId,
        object_id: objectId,
        user_id: userId,
        reminder_at: reminderAt,
        note: note || null,
        status: 'pending',
        created_at: reminder.createdAt,
        updated_at: reminder.updatedAt,
      });
    }

    eventBus.emit('reminder:created', { reminder }, 'local');
    return reminder;
  }

  async getRemindersForBlock(blockId: string): Promise<BlockReminder[]> {
    return await db.reminders.where('blockId').equals(blockId).toArray();
  }

  async getRemindersForObject(objectId: string): Promise<BlockReminder[]> {
    return await db.reminders.where('objectId').equals(objectId).toArray();
  }

  async getPendingReminders(): Promise<BlockReminder[]> {
    return await db.reminders.where('status').equals('pending').toArray();
  }

  async updateReminder(id: string, updates: Partial<BlockReminder>): Promise<void> {
    const userId = this.getUserIdFromLocalStorage();

    const updatedData = {
      ...updates,
      updatedAt: now(),
    };

    await db.reminders.update(id, updatedData);

    const reminder = await db.reminders.get(id);
    if (reminder && userId) {
      const syncData: Record<string, any> = { updated_at: reminder.updatedAt };
      if (updates.status !== undefined) syncData.status = updates.status;
      if (updates.firedAt !== undefined) syncData.fired_at = updates.firedAt;
      if (updates.dismissedAt !== undefined) syncData.dismissed_at = updates.dismissedAt;
      if (updates.note !== undefined) syncData.note = updates.note;
      if (updates.reminderAt !== undefined) syncData.reminder_at = updates.reminderAt;

      await syncEngine.queueChange('block_reminders', 'update', id, syncData);
    }
  }

  async deleteReminder(id: string): Promise<void> {
    const userId = this.getUserIdFromLocalStorage();

    await db.reminders.delete(id);

    if (userId) {
      await syncEngine.queueChange('block_reminders', 'delete', id, {});
    }

    eventBus.emit('reminder:deleted', { id }, 'local');
  }
}

// Singleton instance
export const dataRepository = new DataRepository();
